/**
 * DraftReviewCard — one editable vision/meal-agent draft (Fase 6 + Wave 3).
 *
 * The parent reviews the model's proposal inline in the assistant thread and
 * can edit every field before confirming.
 *   - Homework: title / subject / points / due-date / assignee.
 *   - Event:    title / start / all-day / location / attendee.
 *   - Meal:     title / date / ingredient list / notes, with a live shopping
 *               diff (what will be added to the shared list on confirm).
 *
 * Allergen handling differs by kind (CLAUDE.md — allergies are safety-critical):
 *   - Homework / event: a household-allergen hit is a loud ADVISORY banner; it
 *     never blocks ("ta med nøttefri matpakke" is legitimate).
 *   - Meal: a hit is a HARD-BLOCK. Confirm is disabled until the parent ticks an
 *     explicit override ("Jeg tar ansvar"). The server enforces the same gate
 *     (422 allergen_blocked without override), so this is fail-closed UI over a
 *     fail-closed API. The block keys on the server's create-time scan; the
 *     server re-scans the edited payload at confirm.
 *
 * #158 (steg 8b, Stell_Utkast): kortet er kompakt — typechip (Lekse lilla,
 * Hendelse blå, Middag amber), tittel, «Rediger» og en metalinje («Bella ·
 * frist tir. 24. juni · 15 p»). «Rediger» folder ut dagens skjema med alle
 * feltene og Forkast/Godkjenn for dette utkastet. Skjemaet beholder verdiene
 * når det foldes sammen, og «Lagre alle» bruker dem også da.
 *
 * Allergenflatene står ALLTID synlige, utenfor skjemaet:
 *   - den harde blokken på middager, med «Jeg tar ansvar», er `bad` på
 *     `bad/10` med skjold, som sperren i MealEditSheet (#157), så den ikke
 *     kan forveksles med varselet. Skissen tegner den amber; vi følger appens
 *     andre middagssperre. Til den er krysset av, har hele kortet `bad`-kant;
 *   - varselet på lekser og hendelser er amber (ink på warn-bg, warn-trekant).
 * Parene står i contrast.test.ts.
 *
 * Plain <div> wrapper (the Card primitive does not forward data-testid). Pure /
 * presentational — the current shopping-list texts arrive as a prop so the card
 * needs no query context (keeps it trivially testable).
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  diffIngredients,
  type AiDraftDTO,
  type CreateCalendarEventDraftInput,
  type MemberAdminRow,
} from '@stell/shared';
import { Icon, Pill } from '../../components/primitives';
import { Button } from '../../components/primitives/Button';
import { GLASS_FILL, GLASS_SURFACE } from '../../components/primitives/Card';
import type { DraftPayload } from './useVisionDrafts';

interface DraftReviewCardProps {
  draft: AiDraftDTO;
  members: MemberAdminRow[];
  onConfirm: (payload: DraftPayload, opts?: { override?: boolean }) => void;
  onReviewChange?: (draftId: string, payload: DraftPayload, opts?: { override?: boolean }) => void;
  onDiscard: () => void;
  busy: boolean;
  /** Current shopping-list item texts — drives the meal shopping-diff preview. */
  shoppingTexts?: string[];
  /** «Lagre alle» stoppet på dette utkastet fordi et felt mangler: åpne skjemaet. */
  needsAttention?: boolean;
}

/** datetime-local wants 'YYYY-MM-DDTHH:mm'; ISO may carry an offset suffix. */
function toLocalInput(iso: string): string {
  return iso.slice(0, 16);
}
function fromLocalInput(local: string, originalIso: string): string {
  const offset = /([+-]\d{2}:\d{2}|Z)$/.exec(originalIso)?.[0] ?? '+02:00';
  return `${local}:00${offset}`;
}

const inputCls = 'field focus:outline-none focus:ring-2 focus:ring-primary';
const labelCls = 'flex flex-col gap-1.5';
const labelTextCls = 'text-xs font-bold text-ink-2';
const checkCls = 'm-0 h-5.5 w-5.5 shrink-0 accent-primary';

// «tir. 24. juni». Datoene er kalenderdager uten tidssone (YYYY-MM-DD).
const dateFmt = new Intl.DateTimeFormat('nb-NO', {
  weekday: 'short',
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
});

function formatDay(ymd: unknown): string | null {
  if (typeof ymd !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  return dateFmt.format(new Date(`${ymd}T00:00:00Z`));
}

/**
 * «ons. 24. juni kl. 09:00». Klokkeslettet leses rett fra ISO-strengen, som
 * datetime-local-feltet i skjemaet gjør (`toLocalInput`), så metalinjen og
 * skjemaet aldri viser to forskjellige tider.
 */
function formatStart(iso: unknown, allDay: boolean, at: (time: string) => string): string | null {
  if (typeof iso !== 'string') return null;
  const day = formatDay(iso.slice(0, 10));
  if (!day || allDay) return day;
  const time = /^T(\d{2}:\d{2})/.exec(iso.slice(10))?.[1];
  return time ? `${day} ${at(time)}` : day;
}

export function DraftReviewCard({
  draft,
  members,
  onConfirm,
  onReviewChange,
  onDiscard,
  busy,
  shoppingTexts = [],
  needsAttention = false,
}: DraftReviewCardProps) {
  const { t } = useTranslation();
  const isEvent = draft.kind === 'event';
  const isMeal = draft.kind === 'meal';

  // Local editable copy of the payload.
  const [form, setForm] = useState<Record<string, unknown>>({ ...(draft.payload as Record<string, unknown>) });
  const set = (k: string, v: unknown) => setForm((f) => ({ ...f, [k]: v }));

  // Meal HARD-BLOCK: a flagged meal draft cannot be confirmed without the parent
  // ticking this. Homework/event never block, so the toggle is meal-only.
  const hasAllergen = Boolean(draft.allergen_warning && draft.allergen_warning.length > 0);
  const mealBlocked = isMeal && hasAllergen;
  const [override, setOverride] = useState(false);
  const [editing, setEditing] = useState(false);
  const formId = useId();
  useEffect(() => {
    if (needsAttention) setEditing(true);
  }, [needsAttention]);

  const assignKey = isEvent ? 'attendee_id' : 'assignee_id';
  const assignableMembers = members.filter((m) => !m.archived_at);
  // Original ISO offset is preserved when the parent edits the wall-clock time.
  const originalStartsAt = isEvent ? String((draft.payload as CreateCalendarEventDraftInput).starts_at ?? '') : '';

  // ── meal ingredient editor state ───────────────────────────────────────────
  const ingredients = useMemo<string[]>(
    () => (Array.isArray(form.ingredients) ? (form.ingredients as string[]) : []),
    [form.ingredients],
  );
  const setIngredient = (i: number, v: string) =>
    set('ingredients', ingredients.map((ing, idx) => (idx === i ? v : ing)));
  const removeIngredient = (i: number) => set('ingredients', ingredients.filter((_, idx) => idx !== i));
  const addIngredient = () => set('ingredients', [...ingredients, '']);
  const cleanIngredients = useMemo(() => ingredients.map((s) => s.trim()).filter(Boolean), [ingredients]);
  const diff = isMeal ? diffIngredients(cleanIngredients, shoppingTexts) : { needed: [], have: [] };

  const reviewedPayload = useMemo<DraftPayload>(() => {
    if (isEvent) {
      return {
        title: String(form.title ?? '').trim(),
        starts_at: String(form.starts_at ?? ''),
        all_day: Boolean(form.all_day),
        ...(form.description ? { description: String(form.description) } : {}),
        ...(form.location ? { location: String(form.location) } : {}),
        ...(form.ends_at ? { ends_at: String(form.ends_at) } : {}),
        ...(form.attendee_id ? { attendee_id: String(form.attendee_id) } : {}),
      };
    }
    if (isMeal) {
      return {
        title: String(form.title ?? '').trim(),
        ingredients: cleanIngredients,
        ...(form.date ? { date: String(form.date) } : {}),
        ...(form.notes ? { notes: String(form.notes) } : {}),
      };
    }
    return {
      title: String(form.title ?? '').trim(),
      point_value: Number(form.point_value ?? 0),
      ...(form.subject ? { subject: String(form.subject) } : {}),
      ...(form.description ? { description: String(form.description) } : {}),
      ...(form.due_date ? { due_date: String(form.due_date) } : {}),
      ...(form.assignee_id ? { assignee_id: String(form.assignee_id) } : {}),
    };
  }, [cleanIngredients, form, isEvent, isMeal]);

  const reviewedOpts = useMemo<{ override?: boolean } | undefined>(
    () => (isMeal && mealBlocked && override ? { override: true } : undefined),
    [isMeal, mealBlocked, override],
  );

  useEffect(() => {
    onReviewChange?.(draft.id, reviewedPayload, reviewedOpts);
  }, [draft.id, onReviewChange, reviewedOpts, reviewedPayload]);

  function confirm() {
    onConfirm(reviewedPayload, reviewedOpts);
  }

  const kindLabel = isEvent
    ? t('assistant.vision.kindEvent')
    : isMeal
      ? t('assistant.vision.kindMeal')
      : t('assistant.vision.kindHomework');
  const kindTone = isEvent ? 'blue' : isMeal ? 'warn' : 'lilac';

  const title = String(form.title ?? '').trim();
  const dueDay = formatDay(form.due_date);
  const memberName = (id: unknown) =>
    typeof id === 'string' ? assignableMembers.find((m) => m.id === id)?.short_name : undefined;
  const meta = (
    isEvent
      ? [
          formatStart(form.starts_at, Boolean(form.all_day), (time) =>
            t('assistant.vision.metaAt', { time }),
          ),
          form.location ? String(form.location) : null,
          memberName(form.attendee_id),
        ]
      : isMeal
        ? [
            formatDay(form.date),
            diff.needed.length > 0
              ? t('assistant.vision.metaShopping', { count: diff.needed.length })
              : null,
          ]
        : [
            memberName(form.assignee_id),
            dueDay ? t('assistant.vision.metaDue', { date: dueDay }) : null,
            t('assistant.vision.metaPoints', { count: Number(form.point_value ?? 0) }),
          ]
  ).filter(Boolean);
  const allergens = (draft.allergen_warning ?? []).map((hh) => hh.keyword).join(', ');
  // En blokkert middag har tydelig kant til forelderen har tatt ansvar.
  const cardSurface = mealBlocked && !override ? `${GLASS_FILL} border border-bad/50` : GLASS_SURFACE;

  return (
    <div
      data-testid={`draft-card-${draft.id}`}
      className={`${cardSurface} flex flex-col gap-1.5 rounded-row px-3.5 py-3`}
    >
      <div className="flex items-center gap-2.5">
        <Pill variant={kindTone} className="shrink-0">
          {kindLabel}
        </Pill>
        <h3 className="m-0 min-w-0 flex-1 truncate text-base font-extrabold text-ink">
          {title || t('assistant.vision.untitled')}
        </h3>
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          aria-expanded={editing}
          aria-controls={editing ? formId : undefined}
          aria-label={t('assistant.vision.editAria', { title: title || kindLabel })}
          data-testid={`draft-edit-${draft.id}`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-track text-ink hover:opacity-90"
        >
          <Icon.Edit size={16} />
        </button>
      </div>
      {meta.length > 0 ? (
        <p className="m-0 text-sm text-ink-2" data-testid={`draft-meta-${draft.id}`}>
          {meta.join(' · ')}
        </p>
      ) : null}

      {/* Allergen — meal HARD-BLOCK vs homework/event advisory. Alltid synlig. */}
      {mealBlocked && (
        <div
          role="alert"
          data-testid={`draft-allergen-${draft.id}`}
          className="mt-1 flex flex-col gap-1 rounded-md border border-bad/50 bg-bad/10 px-3 py-2.5 text-bad"
        >
          <p className="m-0 flex items-start gap-2 text-sm font-extrabold">
            <Icon.AlertShield size={18} className="shrink-0" />
            <span>{t('assistant.vision.allergenBlocked', { allergens })}</span>
          </p>
          <label
            className="flex min-h-11 cursor-pointer items-center gap-2.5 text-sm font-extrabold"
            data-testid={`draft-override-${draft.id}`}
          >
            <input
              type="checkbox"
              className={checkCls}
              checked={override}
              onChange={(e) => setOverride(e.target.checked)}
            />
            {t('assistant.vision.allergenOverride')}
          </label>
        </div>
      )}
      {hasAllergen && !mealBlocked && (
        <div
          role="alert"
          data-testid={`draft-allergen-${draft.id}`}
          className="mt-1 flex items-start gap-2 rounded-md bg-warn-bg px-3 py-2.5 text-sm font-bold text-ink"
        >
          <Icon.Warn size={16} className="mt-0.5 shrink-0 text-warn" />
          <span>{t('assistant.vision.allergenWarning', { allergens })}</span>
        </div>
      )}

      {editing ? (
        <div id={formId} data-testid={`draft-form-${draft.id}`} className="mt-2 flex flex-col gap-3">
          {/* Title — all kinds */}
          <label className={labelCls}>
            <span className={labelTextCls}>{t('assistant.vision.fieldTitle')}</span>
            <input
              className={inputCls}
              value={String(form.title ?? '')}
              onChange={(e) => set('title', e.target.value)}
              data-testid={`draft-title-${draft.id}`}
            />
          </label>

          {isEvent && (
            <>
              <label className={labelCls}>
                <span className={labelTextCls}>{t('assistant.vision.fieldStartsAt')}</span>
                <input
                  type="datetime-local"
                  className={inputCls}
                  value={toLocalInput(String(form.starts_at ?? ''))}
                  onChange={(e) => set('starts_at', fromLocalInput(e.target.value, originalStartsAt || e.target.value))}
                  data-testid={`draft-startsat-${draft.id}`}
                />
              </label>
              <label className="flex min-h-11 items-center gap-2.5 text-sm font-bold text-ink">
                <input
                  type="checkbox"
                  className={checkCls}
                  checked={Boolean(form.all_day)}
                  onChange={(e) => set('all_day', e.target.checked)}
                  data-testid={`draft-allday-${draft.id}`}
                />
                {t('assistant.vision.fieldAllDay')}
              </label>
              <label className={labelCls}>
                <span className={labelTextCls}>{t('assistant.vision.fieldLocation')}</span>
                <input
                  className={inputCls}
                  value={String(form.location ?? '')}
                  onChange={(e) => set('location', e.target.value)}
                />
              </label>
            </>
          )}

          {isMeal && (
            <>
              <label className={labelCls}>
                <span className={labelTextCls}>{t('assistant.vision.fieldDate')}</span>
                <input
                  type="date"
                  className={inputCls}
                  value={String(form.date ?? '')}
                  onChange={(e) => set('date', e.target.value)}
                  data-testid={`draft-date-${draft.id}`}
                />
              </label>

              <div className="flex flex-col gap-1.5">
                <span className={labelTextCls}>{t('assistant.vision.fieldIngredients')}</span>
                <div className="flex flex-col gap-2" data-testid={`draft-ingredients-${draft.id}`}>
                  {ingredients.map((ing, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        className={inputCls}
                        value={ing}
                        onChange={(e) => setIngredient(i, e.target.value)}
                        aria-label={t('assistant.vision.ingredientN', { n: i + 1 })}
                        data-testid={`draft-ingredient-${draft.id}-${i}`}
                      />
                      <button
                        type="button"
                        onClick={() => removeIngredient(i)}
                        aria-label={t('assistant.vision.removeIngredient')}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-inset hover:text-ink"
                        data-testid={`draft-ingredient-remove-${draft.id}-${i}`}
                      >
                        <Icon.Trash size={18} />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={addIngredient}
                  data-testid={`draft-ingredient-add-${draft.id}`}
                  className="flex min-h-11 items-center gap-1.5 self-start text-sm font-bold text-accent-text hover:underline"
                >
                  <Icon.Plus size={16} />
                  {t('assistant.vision.addIngredient')}
                </button>
              </div>

              {/* Shopping diff preview */}
              <div
                className="flex flex-col gap-1 rounded-md bg-inset p-3 text-sm"
                data-testid={`draft-diff-${draft.id}`}
              >
                <div className="flex items-center gap-1.5 font-bold text-ink">
                  <Icon.Cart size={16} />
                  <span>{t('assistant.vision.diffTitle')}</span>
                </div>
                {diff.needed.length > 0 ? (
                  <p className="m-0 text-ink">
                    {t('assistant.vision.diffNeeded', { items: diff.needed.join(', ') })}
                  </p>
                ) : (
                  <p className="m-0 text-ink-2">{t('assistant.vision.diffNoneNeeded')}</p>
                )}
                {diff.have.length > 0 && (
                  <p className="m-0 text-ink-2">{t('assistant.vision.diffHave', { items: diff.have.join(', ') })}</p>
                )}
              </div>

              <label className={labelCls}>
                <span className={labelTextCls}>{t('assistant.vision.fieldNotes')}</span>
                <input
                  className={inputCls}
                  value={String(form.notes ?? '')}
                  onChange={(e) => set('notes', e.target.value)}
                  data-testid={`draft-notes-${draft.id}`}
                />
              </label>
            </>
          )}

          {!isEvent && !isMeal && (
            <>
              <label className={labelCls}>
                <span className={labelTextCls}>{t('assistant.vision.fieldSubject')}</span>
                <input
                  className={inputCls}
                  value={String(form.subject ?? '')}
                  onChange={(e) => set('subject', e.target.value)}
                  data-testid={`draft-subject-${draft.id}`}
                />
              </label>
              <div className="flex gap-3">
                <label className={`${labelCls} min-w-0 flex-1`}>
                  <span className={labelTextCls}>{t('assistant.vision.fieldPoints')}</span>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    className={inputCls}
                    value={Number(form.point_value ?? 0)}
                    onChange={(e) => set('point_value', Number(e.target.value))}
                    data-testid={`draft-points-${draft.id}`}
                  />
                </label>
                <label className={`${labelCls} min-w-0 flex-1`}>
                  <span className={labelTextCls}>{t('assistant.vision.fieldDueDate')}</span>
                  <input
                    type="date"
                    className={inputCls}
                    value={String(form.due_date ?? '')}
                    onChange={(e) => set('due_date', e.target.value)}
                    data-testid={`draft-duedate-${draft.id}`}
                  />
                </label>
              </div>
            </>
          )}

          {/* Assignee — homework + event only (meals are household-wide) */}
          {!isMeal && (
            <label className={labelCls}>
              <span className={labelTextCls}>{t('assistant.vision.fieldAssignee')}</span>
              <select
                className={inputCls}
                value={String(form[assignKey] ?? '')}
                onChange={(e) => set(assignKey, e.target.value || undefined)}
                data-testid={`draft-assignee-${draft.id}`}
              >
                <option value="">{t('assistant.vision.assigneeNone')}</option>
                {assignableMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.short_name}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="secondary"
              size={44}
              onClick={onDiscard}
              disabled={busy}
              data-testid={`draft-discard-${draft.id}`}
            >
              {t('assistant.vision.discard')}
            </Button>
            <Button
              variant="primary"
              size={44}
              onClick={confirm}
              disabled={busy || !title || (mealBlocked && !override)}
              data-testid={`draft-confirm-${draft.id}`}
            >
              <Icon.Check size={16} />
              {t('assistant.vision.confirm')}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
